import { Sparkles, Plus, Check, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FIELD } from "@/constants/testIds";

function Row({ label, value, onClick, testid }) {
  return (
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-forest-mid">{label}</p>
        <p className="text-sm text-forest-dark">{value}</p>
      </div>
      <Button type="button" size="sm" variant="outline" className="h-7 shrink-0" onClick={onClick} data-testid={testid}>
        <Check className="mr-1 h-3.5 w-3.5" /> Use
      </Button>
    </div>
  );
}

export function FieldAiSuggestions({ ai, busy, onApplyCaption, onApplyNotes, onAddQuantities, onAddLabour, onApplyAll }) {
  if (busy) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-forest-mid/30 bg-sage-surface p-3 text-sm text-forest-dark" data-testid={FIELD.aiBusy}>
        <Loader2 className="h-4 w-4 animate-spin text-forest-mid" /> AI is reading your photo…
      </div>
    );
  }
  if (!ai) return null;
  const hasForm = ai.caption || ai.notes || ai.quantities?.length || ai.labour?.length;
  const flagged = ai.quality && ai.quality.flag && ai.quality.flag !== "ok";
  if (!hasForm && !ai.annotations?.length && !flagged) return null;
  return (
    <div className="space-y-2.5 rounded-xl border border-forest-mid/30 bg-sage-surface p-3" data-testid={FIELD.aiPanel}>
      {flagged && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800" data-testid={FIELD.aiQuality}>
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{ai.quality.message || "This photo may be low quality."} Your supervisor will see this flag — retake it if you can.</span>
        </div>
      )}
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 font-heading text-sm font-bold text-forest-dark">
          <Sparkles className="h-4 w-4 text-forest-mid" /> AI documentation
        </span>
        {hasForm && (
          <Button type="button" size="sm" className="h-7 bg-forest-mid hover:bg-forest-dark" onClick={onApplyAll} data-testid={FIELD.aiApplyAll}>
            <Check className="mr-1 h-3.5 w-3.5" /> Apply all
          </Button>
        )}
      </div>
      {ai.caption && <Row label="Caption" value={ai.caption} onClick={() => onApplyCaption(ai.caption)} testid={FIELD.aiApplyCaption} />}
      {ai.notes && <Row label="Notes" value={ai.notes} onClick={() => onApplyNotes(ai.notes)} testid={FIELD.aiApplyNotes} />}
      {ai.quantities?.length > 0 && (
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-forest-mid">Quantities</p>
            <p className="text-sm text-forest-dark">{ai.quantities.map((q) => `${q.item}${q.quantity ? ` ${q.quantity}` : ""}${q.unit ? ` ${q.unit}` : ""}`).join(", ")}</p>
          </div>
          <Button type="button" size="sm" variant="outline" className="h-7 shrink-0" onClick={() => onAddQuantities(ai.quantities)} data-testid={FIELD.aiAddQty}>
            <Plus className="mr-1 h-3.5 w-3.5" /> Add
          </Button>
        </div>
      )}
      {ai.labour?.length > 0 && (
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-forest-mid">Labour</p>
            <p className="text-sm text-forest-dark">{ai.labour.map((l) => `${l.role}${l.hours ? ` ${l.hours}h` : ""} ×${l.workers || 1}`).join(", ")}</p>
          </div>
          <Button type="button" size="sm" variant="outline" className="h-7 shrink-0" onClick={() => onAddLabour(ai.labour)} data-testid={FIELD.aiAddLabour}>
            <Plus className="mr-1 h-3.5 w-3.5" /> Add
          </Button>
        </div>
      )}
      {ai.annotations?.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {ai.annotations.length} suggested marker{ai.annotations.length > 1 ? "s" : ""} drawn on the photo — adjust, keep, or clear below.
        </p>
      )}
    </div>
  );
}
